'use strict';

const _ = require('lodash');
const Enum = require('./enum');
const { SystemUsers } = require('./users');
const {
  LockActions,
  LockTamperedAction,
  LockCredentialActions,
  LockAccessoryEventAction,
  LockPartnerSettingActions,
  DoorActions,
  ShabbatModeActions,
} = require('./locks');
const { MERCURY_LOG_ACTIONS, UNITY_LOG_ACTIONS } = require('./keypads');
const Devices = require('./devices').Devices;
const { LockCredentialTypes } = require('./credentials');
const houses = require('./houses');
const { LockControlPartner } = require('./orchestra');
const { WaActivityFeedActions } = require('./wasystems');

/**
 * Defines action keys.
 *
 * @enum
 * @readonly
 */
module.exports.ActionKeys = Object.create(Enum, {
  AUTO_LOCK: { value: 'auto_lock', enumerable: true },
  BLE_LOCK: { value: 'ble_lock', enumerable: true },
  BLE_SECURE: { value: 'ble_secure', enumerable: true },
  BLE_UNLOCK: { value: 'ble_unlock', enumerable: true },

  CREDENTIAL_DELETE: { value: 'credential_delete', enumerable: true },
  CREDENTIAL_DISABLE: { value: 'credential_disable', enumerable: true },
  CREDENTIAL_ENABLE: { value: 'credential_enable', enumerable: true },
  CREDENTIAL_INVALID: { value: 'credential_invalid', enumerable: true },
  CREDENTIAL_LOAD: { value: 'credential_load', enumerable: true },
  CREDENTIAL_LOCK: { value: 'credential_lock', enumerable: true },
  CREDENTIAL_SECURE: { value: 'credential_secure', enumerable: true },
  CREDENTIAL_UNLOCK: { value: 'credential_unlock', enumerable: true },
  CREDENTIAL_UPDATE: { value: 'credential_update', enumerable: true },

  DOORBELL_CALL_DECLINED: { value: 'doorbell_call_declined', enumerable: true },
  DOORBELL_CALL_HANGUP: { value: 'doorbell_call_hangup', enumerable: true },
  DOORBELL_CALL_INITIATED: { value: 'doorbell_call_initiated', enumerable: true },
  DOORBELL_CALL_MISSED: { value: 'doorbell_call_missed', enumerable: true },
  DOORBELL_MOTION_DETECTED: { value: 'doorbell_motion_detected', enumerable: true },
  DOORBELL_USER_ADD: { value: 'doorbell_user_add', enumerable: true },
  DOORBELL_USER_REMOVE: { value: 'doorbell_user_remove', enumerable: true },
  DOORBELL_LOW_BATTERY: { value: 'doorbell_low_battery', enumerable: true },

  DOOR_CLOSE: { value: 'door_close', enumerable: true },
  DOOR_OPEN: { value: 'door_open', enumerable: true },
  ESCAPE_UNLOCK: { value: 'escape_unlock', enumerable: true },
  FINGER_LOCK: { value: 'finger_lock', enumerable: true },
  FINGER_UNLOCK: { value: 'finger_unlock', enumerable: true },
  FINGER_INVALID: { value: 'finger_invalid', enumerable: true },

  HOMEKIT_LOCK: { value: 'homekit_lock', enumerable: true },
  HOMEKIT_UNLATCH: { value: 'homekit_unlatch', enumerable: true },
  HOMEKIT_UNLOCK: { value: 'homekit_unlock', enumerable: true },

  HOMEKEY_LOCK: { value: 'homekey_lock', enumerable: true },
  HOMEKEY_UNLATCH: { value: 'homekey_unlatch', enumerable: true },
  HOMEKEY_UNLOCK: { value: 'homekey_unlock', enumerable: true },

  LATCH: { value: 'latch', enumerable: true },
  LOCK_USER_ADD: { value: 'lock_user_add', enumerable: true },
  LOCK_USER_REMOVE: { value: 'lock_user_remove', enumerable: true },
  LOW_BATTERY_UNLOCK: { value: 'low_battery_unlock', enumerable: true },
  MANUAL_LOCK: { value: 'manual_lock', enumerable: true },
  MANUAL_UNLOCK: { value: 'manual_unlock', enumerable: true },
  NEST_CAMERA: { value: 'nest_camera', enumerable: true },
  ONE_TOUCH_LOCK: { value: 'one_touch_lock', enumerable: true },

  PIN_ADDED: { value: 'pin_added', enumerable: true },
  PIN_DISABLED: { value: 'disabledpin', enumerable: true }, // legacy, to be replaced by CREDENTIAL_DISABLE when ALL apps support it
  PIN_ENABLED: { value: 'enabledpin', enumerable: true }, // legacy, to be replaced by CREDENTIAL_ENABLE when ALL apps support it
  PIN_INVALID: { value: 'pin_invalid', enumerable: true },
  PIN_LOCK: { value: 'pin_lock', enumerable: true },
  PIN_REMOVED: { value: 'pin_removed', enumerable: true },
  PIN_SECURE: { value: 'pin_secure', enumerable: true },
  PIN_UNLOCK: { value: 'pin_unlock', enumerable: true },

  REMOTE_LOCK: { value: 'remote_lock', enumerable: true },
  REMOTE_SECURE: { value: 'remote_secure', enumerable: true },
  REMOTE_UNLATCH: { value: 'remote_unlatch', enumerable: true },
  REMOTE_UNLOCK: { value: 'remote_unlock', enumerable: true },

  RF_LOCK: { value: 'rf_lock', enumerable: true },
  RF_SECURE: { value: 'rf_secure', enumerable: true },
  RF_UNLOCK: { value: 'rf_unlock', enumerable: true },

  RFID_HID_LOCK: { value: 'rfid_hid_lock', enumerable: true },
  RFID_HID_UNLOCK: { value: 'rfid_hid_unlock', enumerable: true },

  FACE_LOCK: { value: 'face_lock', enumerable: true },
  FACE_UNLOCK: { value: 'face_unlock', enumerable: true },

  TAMPER_CLEAR: { value: 'tamper_clear', enumerable: true },
  TAMPER_FORCE_OPEN: { value: 'tamper_force_open', enumerable: true },
  TAMPER_FRONT_REMOVED: { value: 'tamper_front_removed', enumerable: true },
  TAMPER_TOO_MANY_AUTH_ERROR: { value: 'tamper_too_many_auth_error', enumerable: true },

  UNKNOWN: { value: 'unknown', enumerable: true },
  UNLATCH: { value: 'unlatch', enumerable: true },
  USER_ADD_TO_RULE: { value: 'user_add_to_rule', enumerable: true },
  USER_PRIVILEGE_CHANGE: { value: 'user_privilege_change', enumerable: true },
  USER_ADD: { value: 'user_add', enumerable: true },
  USER_REMOVE: { value: 'user_remove', enumerable: true },
  USER_REMOVE_FROM_RULE: { value: 'user_remove_from_rule', enumerable: true },
  LINKED_UNLOCK: { value: 'linked_unlock', enumerable: true },
  LINKED_LOCK: { value: 'linked_lock', enumerable: true },
  LINKED_SECURE: { value: 'linked_secure', enumerable: true },

  ORCHESTRA_SERVICE_STARTED: { value: 'orchestra_service_started', enumerable: true },
  ORCHESTRA_SERVICE_COMPLETED: { value: 'orchestra_service_completed', enumerable: true },
  ORCHESTRA_ACCESS_GRANTED: { value: 'orchestra_access_granted', enumerable: true },
  ORCHESTRA_ACCESS_REVOKED: { value: 'orchestra_access_revoked', enumerable: true },

  LOCK_ACCESSORY_BUTTON_PRESS: { value: 'lock_accessory_button_press', enumerable: true },
  LOCK_ACCESSORY_MOTION_DETECT: { value: 'lock_accessory_motion_detect', enumerable: true },

  SHABBAT_MODE_ENABLED: { value: 'shabbat_mode_enabled', enumerable: true },
  SHABBAT_MODE_DISABLED: { value: 'shabbat_mode_disabled', enumerable: true },

  // From an activity log database document.
  fromLog: {
    value: function(log, deviceTypeField = 'DeviceType') {
      let isRemote = _.get(log, 'info.remote', false);
      let isKeypad = _.get(log, 'info.keypad', false);
      let hasUnlatchCapability = _.get(log, 'capabilities.unlatch', false);
      const isDoorbell = [Devices.DOORBELL, Devices.DOORBELL_V2, Devices.DOORBELL_V3].includes(
        _.get(log, deviceTypeField)
      );
      const isWaSystem = Devices.WA_SYSTEM === _.get(log, deviceTypeField);
      const credentialType = _.get(log, 'info.credentialType', false);
      const agent = _.get(log, 'info.agent', false);
      const integration = _.get(log, 'info.integration', null);

      switch (true) {
        case credentialType && log.action === LockCredentialActions.UNLOCK:
          if (credentialType === LockCredentialTypes.RF) {
            return this.RF_UNLOCK;
          }
          if (credentialType === LockCredentialTypes.FINGER) {
            return this.FINGER_UNLOCK;
          }
          if (credentialType === LockCredentialTypes.RFID_HID) {
            return this.RFID_HID_UNLOCK;
          }
          if (credentialType === LockCredentialTypes.FACE) {
            return this.FACE_UNLOCK;
          }

          return this.PIN_UNLOCK;
        case credentialType && log.action === LockCredentialActions.LOCK:
          if (credentialType === LockCredentialTypes.RF) {
            return this.RF_LOCK;
          }
          if (credentialType === LockCredentialTypes.FINGER) {
            return this.FINGER_LOCK;
          }
          if (credentialType === LockCredentialTypes.RFID_HID) {
            return this.RFID_HID_LOCK;
          }
          if (credentialType === LockCredentialTypes.FACE) {
            return this.FACE_LOCK;
          }

          return this.PIN_LOCK;
        case credentialType && log.action === LockCredentialActions.INVALID: {
          if (credentialType === LockCredentialTypes.FINGER) {
            return this.FINGER_INVALID;
          }

          return this.CREDENTIAL_INVALID;
        }
        case credentialType && log.action === LockCredentialActions.LOAD:
          return this.CREDENTIAL_LOAD;
        case credentialType && log.action === LockCredentialActions.ENABLE:
          return this.CREDENTIAL_ENABLE;
        case credentialType && log.action === LockCredentialActions.DELETE:
          return this.CREDENTIAL_DELETE;
        case credentialType && log.action === LockCredentialActions.DISABLE:
          return this.CREDENTIAL_DISABLE;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadUpdatePin:
        case credentialType && log.action === LockCredentialActions.UPDATE:
          return this.CREDENTIAL_UPDATE;
        case credentialType && log.action === LockCredentialActions.SECURE:
          if (credentialType === LockCredentialTypes.PIN) {
            return this.PIN_SECURE;
          }
          if (credentialType === LockCredentialTypes.RF) {
            return this.RF_SECURE;
          }

          return this.CREDENTIAL_SECURE;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadOpen:
          return this.PIN_UNLOCK;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadClose:
          return this.PIN_LOCK;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadWrongCode:
          return this.PIN_INVALID;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadRemovePin:
        case isKeypad && log.action === UNITY_LOG_ACTIONS.CodeDeleted:
        case isKeypad && log.action === UNITY_LOG_ACTIONS.CodeDeletedv2:
          return this.PIN_REMOVED;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadAddPin:
        case isKeypad && log.action === UNITY_LOG_ACTIONS.CodeAdded:
        case isKeypad && log.action === UNITY_LOG_ACTIONS.CodeAddedv2:
          return this.PIN_ADDED;
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadOneTouch:
          return this.ONE_TOUCH_LOCK;
        // legacy, to be replaced by CREDENTIAL_ENABLE when ALL apps support it
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadEnabledPin:
          return this.PIN_ENABLED;
        // legacy, to be replaced by CREDENTIAL_DISABLE when ALL apps support it
        case isKeypad && log.action === MERCURY_LOG_ACTIONS.KeypadDisabledPin:
          return this.PIN_DISABLED;
        //  EURO-02 sends an unlock action, this is the latched state for a EURO-02, thus when action is unlock and has unlatch capability, unlock = latch. SER-4361
        case hasUnlatchCapability && log.action === LockActions.UNLOCK:
          return this.LATCH;
        case log.callingUserID === SystemUsers.MANUAL_UNLOCK && log.action === LockActions.UNLATCH:
          return this.UNLATCH;
        case log.callingUserID === SystemUsers.MANUAL_LOCK:
        case log.callingUserID === SystemUsers.INSIDE_BUTTON_LOCK:
          return this.MANUAL_LOCK;
        case log.callingUserID === SystemUsers.MANUAL_UNLOCK:
        case log.callingUserID === SystemUsers.INSIDE_BUTTON_UNLOCK:
          return this.MANUAL_UNLOCK;
        case log.callingUserID === SystemUsers.AUTO_RELOCK:
          return this.AUTO_LOCK;
        case log.callingUserID === SystemUsers.LOW_BAT_AUTO_UNLOCK:
          return this.LOW_BATTERY_UNLOCK;
        case log.action === 'dooropen':
          return this.DOOR_OPEN;
        case log.action === 'doorclosed':
          return this.DOOR_CLOSE;
        // return 'nest_camera';
        case isDoorbell && log.action === 'add':
          return this.DOORBELL_USER_ADD;
        case isDoorbell && log.action === 'remove':
          return this.DOORBELL_USER_REMOVE;
        case isDoorbell && log.action === 'doorbell_call_hangup':
          return this.DOORBELL_CALL_HANGUP;
        case isDoorbell && log.action === 'doorbell_call_initiated':
          return this.DOORBELL_CALL_INITIATED;
        case isDoorbell && log.action === 'doorbell_call_missed':
          return this.DOORBELL_CALL_MISSED;
        case isDoorbell && log.action === 'doorbell_call_declined':
          return this.DOORBELL_CALL_DECLINED;
        case isDoorbell && log.action === 'doorbell_motion_detected':
          return this.DOORBELL_MOTION_DETECTED;
        case isDoorbell && log.action === 'doorbell_low_battery':
          return this.DOORBELL_LOW_BATTERY;
        case log.action === 'add':
          return this.LOCK_USER_ADD;
        case log.action === 'remove':
          return this.LOCK_USER_REMOVE;
        case log.action === 'addtorule':
          return this.USER_ADD_TO_RULE;

        case log.action === 'removefromrule':
          return this.USER_REMOVE_FROM_RULE;

        case log.action === 'privilegechange':
          return this.USER_PRIVILEGE_CHANGE;
        case log.action === 'user_add':
          return this.USER_ADD;
        case log.action === 'user_remove':
          return this.USER_REMOVE;

        case log.action === LockActions.UNLATCH:
          if (agent === houses.HOMEKIT_AGENT) {
            return this.HOMEKIT_UNLATCH;
          } else if (agent === houses.HOMEKEY_AGENT) {
            return this.HOMEKEY_UNLATCH;
          } else if (isRemote) {
            return this.REMOTE_UNLATCH;
          }

          return this.UNLATCH;

        case log.action === LockActions.LOCK:
          if (agent === houses.HOMEKIT_AGENT) {
            return this.HOMEKIT_LOCK;
          } else if (agent === houses.HOMEKEY_AGENT) {
            return this.HOMEKEY_LOCK;
          } else if (isRemote) {
            return this.REMOTE_LOCK;
          } else if (log.callingUserID === SystemUsers.LINKED_OPERATE) {
            return this.LINKED_LOCK;
          }

          return this.BLE_LOCK;

        case log.action === LockActions.CALIBRATE:
        case log.action === LockActions.UNLOCK:
          if (agent === houses.HOMEKIT_AGENT) {
            return this.HOMEKIT_UNLOCK;
          } else if (agent === houses.HOMEKEY_AGENT) {
            return this.HOMEKEY_UNLOCK;
          } else if (isRemote) {
            return this.REMOTE_UNLOCK;
          } else if (log.callingUserID === SystemUsers.LINKED_OPERATE) {
            return this.LINKED_UNLOCK;
          }

          return this.BLE_UNLOCK;

        case log.action === LockActions.SECURE:
          if (isRemote) {
            return this.REMOTE_SECURE;
          } else if (log.callingUserID === SystemUsers.LINKED_OPERATE) {
            return this.LINKED_SECURE;
          }

          return this.BLE_SECURE;
        case log.action === DoorActions.OPERATE:
          return DoorActions.OPERATE;
        case log.action === 'camera':
          return 'camera';
        case log.action === LockTamperedAction.ForceOpen:
          return this.TAMPER_FORCE_OPEN;
        case log.action === LockTamperedAction.TooManyAuthErrors:
          return this.TAMPER_TOO_MANY_AUTH_ERROR;
        case log.action === LockTamperedAction.FrontRemoved:
          return this.TAMPER_FRONT_REMOVED;
        case log.action === LockTamperedAction.Clear:
          return this.TAMPER_CLEAR;
        case log.action === LockActions.ESCAPE_UNLOCK:
          return this.ESCAPE_UNLOCK;
        case integration === 'orchestra' && log.action === LockControlPartner.ActivityFeedActions.SERVICE_STARTED:
          return this.ORCHESTRA_SERVICE_STARTED;
        case integration === 'orchestra' && log.action === LockControlPartner.ActivityFeedActions.SERVICE_COMPLETED:
          return this.ORCHESTRA_SERVICE_COMPLETED;
        case integration === 'orchestra' && log.action === LockControlPartner.ActivityFeedActions.ACCESS_GRANTED:
          return this.ORCHESTRA_ACCESS_GRANTED;
        case integration === 'orchestra' && log.action === LockControlPartner.ActivityFeedActions.ACCESS_REVOKED:
          return this.ORCHESTRA_ACCESS_REVOKED;
        case log.action === LockAccessoryEventAction.BUTTON_PRESS:
          return this.LOCK_ACCESSORY_BUTTON_PRESS;
        case log.action === LockAccessoryEventAction.MOTION_DETECT:
          return this.LOCK_ACCESSORY_MOTION_DETECT;
        case LockPartnerSettingActions.toArray().includes(log.action):
          return log.action;
        case log.DeviceType === Devices.ALARM:
          return log.action;
        case log.action === ShabbatModeActions.ENABLE:
          return this.SHABBAT_MODE_ENABLED;
        case log.action === ShabbatModeActions.DISABLE:
          return this.SHABBAT_MODE_DISABLED;
        case isWaSystem && WaActivityFeedActions.toArray().includes(log.action):
          return log.action;
        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },
  // From an activity log document that was sent by ACS activity feed v2.
  fromAcsV2Log: {
    value: function(log) {
      return this.fromLog(log, 'deviceType');
    },
    enumerable: false,
  },
  toTitle: {
    value: function(actionKey, deviceName, otherFirstName, otherLastName) {
      switch (actionKey) {
        case this.MANUAL_LOCK:
          return `${deviceName} locked manually`;

        case this.MANUAL_UNLOCK:
          return `${deviceName} unlocked manually`;

        case this.AUTO_LOCK:
          return `${deviceName} Auto-Locked`;

        case this.PIN_LOCK:
          return `${deviceName} locked with entry code`;

        case this.PIN_UNLOCK:
          return `${deviceName} unlocked with entry code`;

        case this.ONE_TOUCH_LOCK:
          return `One-Touch Lock at ${deviceName}`;

        case this.PIN_INVALID:
          return `Invalid entry code used at ${deviceName}`;

        case this.PIN_ADDED:
          return `${deviceName} entry code created for ${otherFirstName} ${otherLastName}`;

        case this.PIN_REMOVED:
          return `${deviceName} entry code removed`;

        case this.REMOTE_LOCK:
          return `${deviceName} locked remotely`;

        case this.REMOTE_UNLOCK:
          return `${deviceName} unlocked remotely`;

        case this.DOOR_OPEN:
          return `${deviceName} opened`;

        case this.DOOR_CLOSE:
          return `${deviceName} closed`;

        case this.DOORBELL_CALL_HANGUP:
          return `${deviceName} answered`;

        case this.DOORBELL_CALL_INITIATED:
          return `${deviceName} live stream viewed`;

        case this.DOORBELL_CALL_MISSED:
          return `Missed visitor at ${deviceName}`;

        case this.DOORBELL_CALL_DECLINED:
          return `${deviceName} declined`;

        case this.DOORBELL_MOTION_DETECTED:
          return `Motion detected at ${deviceName}`;

        case this.NEST_CAMERA:
          return `Nest detected motion at ${deviceName}`;

        case this.LOCK_USER_ADD:
          return `${otherFirstName} ${otherLastName} given access to ${deviceName}`;

        case this.LOCK_USER_REMOVE:
          return `${otherFirstName} ${otherLastName} removed from ${deviceName}`;

        case this.USER_ADD_TO_RULE:
          return `${otherFirstName} ${otherLastName} added to access rule`;

        case this.USER_REMOVE_FROM_RULE:
          return `${otherFirstName} ${otherLastName} removed from access rule`;

        case this.USER_PRIVILEGE_CHANGE:
          return `Access changed for ${otherFirstName} ${otherLastName}`;

        case this.DOORBELL_USER_ADD:
          return `${otherFirstName} ${otherLastName} given access to ${deviceName}`;

        case this.DOORBELL_USER_REMOVE:
          return `${otherFirstName} ${otherLastName} removed from ${deviceName}`;

        case this.BLE_LOCK:
          return `${deviceName} locked`;

        case this.BLE_UNLOCK:
          return `${deviceName} unlocked`;

        default:
          return '';
      }
    },
  },
});

/**
 * stock image that mimics the image object returned by successful upload to image hosting service
 *
 * @type {{bytes: number, width: number, format: string, resource_type: string, secure_url: string, created_at: Date, type: string, url: string, height: number}}
 */
module.exports.fallbackVideoThumbnail = Object.freeze({
  width: 720,
  height: 576,
  format: 'jpg',
  resource_type: 'image',
  created_at: new Date(),
  bytes: 11078,
  type: 'stock-image-placeholder',
  url: 'http://s3-us-west-2.amazonaws.com/com.august.images/hydra-stock-placeholder.jpg',
  secure_url: 'https://s3-us-west-2.amazonaws.com/com.august.images/hydra-stock-placeholder.jpg',
});
